/**
 * Scene document operations — tree edit, find, serialize (Godot .tscn analog as JSON).
 */

import {
  createNode,
  type NodeType,
  type SceneKind,
  type SceneNodeData,
  type SceneResource,
  type ScriptRef,
  uid,
} from './types';

export function cloneNode(node: SceneNodeData): SceneNodeData {
  return JSON.parse(JSON.stringify(node)) as SceneNodeData;
}

export function walkNodes(root: SceneNodeData, fn: (n: SceneNodeData, parent: SceneNodeData | null) => void): void {
  const rec = (n: SceneNodeData, p: SceneNodeData | null) => {
    fn(n, p);
    for (const c of n.children) rec(c, n);
  };
  rec(root, null);
}

export function findNode(root: SceneNodeData, id: string): SceneNodeData | null {
  let hit: SceneNodeData | null = null;
  walkNodes(root, (n) => {
    if (n.id === id) hit = n;
  });
  return hit;
}

export function findNodeByPath(root: SceneNodeData, path: string): SceneNodeData | null {
  const parts = path.replace(/^\//, '').split('/').filter(Boolean);
  if (!parts.length) return root;
  let i = 0;
  let cur: SceneNodeData | null = root;
  if (parts[0] === root.name) i = 1;
  for (; i < parts.length; i++) {
    if (!cur) return null;
    cur = cur.children.find((c) => c.name === parts[i]) ?? null;
  }
  return cur;
}

export function findParent(
  root: SceneNodeData,
  id: string,
): { parent: SceneNodeData | null; index: number } | null {
  if (root.id === id) return { parent: null, index: 0 };
  let result: { parent: SceneNodeData | null; index: number } | null = null;
  walkNodes(root, (n) => {
    const idx = n.children.findIndex((c) => c.id === id);
    if (idx >= 0) result = { parent: n, index: idx };
  });
  return result;
}

export function uniqueChildName(parent: SceneNodeData, base: string): string {
  const names = new Set(parent.children.map((c) => c.name));
  if (!names.has(base)) return base;
  let i = 2;
  while (names.has(`${base}${i}`)) i++;
  return `${base}${i}`;
}

export function addChild(parent: SceneNodeData, type: NodeType, name?: string): SceneNodeData {
  const node = createNode(type, name ? uniqueChildName(parent, name) : uniqueChildName(parent, type));
  parent.children.push(node);
  parent.collapsed = false;
  return node;
}

export function removeNode(root: SceneNodeData, id: string): boolean {
  if (root.id === id) return false; // cannot remove root
  const loc = findParent(root, id);
  if (!loc || !loc.parent) return false;
  loc.parent.children.splice(loc.index, 1);
  return true;
}

export function reparentNode(
  root: SceneNodeData,
  nodeId: string,
  newParentId: string,
  index?: number,
): boolean {
  if (nodeId === root.id) return false;
  const node = findNode(root, nodeId);
  const newParent = findNode(root, newParentId);
  if (!node || !newParent) return false;
  // Prevent parenting into self/descendant
  let isDesc = false;
  walkNodes(node, (n) => {
    if (n.id === newParentId) isDesc = true;
  });
  if (isDesc) return false;
  if (!removeNode(root, nodeId)) return false;
  node.name = uniqueChildName(newParent, node.name);
  if (index === undefined || index < 0 || index > newParent.children.length) {
    newParent.children.push(node);
  } else {
    newParent.children.splice(index, 0, node);
  }
  return true;
}

export function moveNodeSibling(root: SceneNodeData, id: string, dir: -1 | 1): boolean {
  const loc = findParent(root, id);
  if (!loc || !loc.parent) return false;
  const arr = loc.parent.children;
  const j = loc.index + dir;
  if (j < 0 || j >= arr.length) return false;
  const [n] = arr.splice(loc.index, 1);
  arr.splice(j, 0, n);
  return true;
}

export function duplicateNode(root: SceneNodeData, id: string): SceneNodeData | null {
  const loc = findParent(root, id);
  const node = findNode(root, id);
  if (!node || !loc?.parent) return null;
  const copy = rekeyTree(cloneNode(node));
  copy.name = uniqueChildName(loc.parent, node.name);
  loc.parent.children.splice(loc.index + 1, 0, copy);
  return copy;
}

function rekeyTree(node: SceneNodeData): SceneNodeData {
  node.id = uid('node');
  node.children = node.children.map((c) => rekeyTree(c));
  return node;
}

export function createEmptyScene(opts: {
  name: string;
  kind: SceneKind;
  path: string;
  rootType?: NodeType;
}): SceneResource {
  const root = createNode(opts.rootType ?? 'Node3D', opts.name);
  return {
    format: 1,
    id: uid('scene'),
    name: opts.name,
    kind: opts.kind,
    path: opts.path,
    root,
    scripts: {},
    updatedAt: new Date().toISOString(),
    builtin: false,
  };
}

export function attachScript(
  scene: SceneResource,
  nodeId: string,
  script: Omit<ScriptRef, 'id'> & { id?: string },
): ScriptRef {
  const node = findNode(scene.root, nodeId);
  if (!node) throw new Error('Node not found');
  const id = script.id || uid('script');
  const ref: ScriptRef = {
    id,
    path: script.path,
    language: 'js',
    source: script.source,
  };
  scene.scripts[id] = ref;
  node.scriptId = id;
  scene.updatedAt = new Date().toISOString();
  return ref;
}

export function detachScript(scene: SceneResource, nodeId: string): void {
  const node = findNode(scene.root, nodeId);
  if (!node) return;
  node.scriptId = null;
  scene.updatedAt = new Date().toISOString();
}

export function serializeScene(scene: SceneResource): string {
  return JSON.stringify(scene, null, 2);
}

export function parseScene(json: string): SceneResource {
  const s = JSON.parse(json) as SceneResource;
  if (s.format !== 1 || !s.root) throw new Error('Invalid scene format');
  return s;
}

export function listNodeCount(root: SceneNodeData): number {
  let n = 0;
  walkNodes(root, () => {
    n++;
  });
  return n;
}
